/*
  ONX cart drawer enhancements (snippets/onx-cart-drawer.liquid). Vanilla, namespaced, null guarded.
  It only ENHANCES. It never replaces the theme's cart handling: theme.CartDrawer / theme.CartForm still render the
  lines, change quantities, build the subtotal and submit the form to checkout. If this file fails to load or throws,
  the drawer still works (the markup and CSS do not depend on it).

  What it does after every render of [data-products]:
    1. writes the item count next to the title: "CART (3)" (the count is cart.item_count, same number as the header badge)
    2. sale display (Figma): struck through "was" total, current line total and the red "-NN%" tag, for compare at price
       discounts and for line level discounts. The theme's cart item markup only knows original vs final price, so the
       compare at price comes from /products/<handle>.js (cached) and the cents from /cart.js, matched by line key.
       Prices shown are LINE totals (price x quantity), so they add up to the subtotal; the old drawer showed unit prices.
    3. at quantity 1 the minus button removes the line (theme behaviour), so its accessible name becomes "Remove"
    4. announces the new count and subtotal in a polite live region
  Motion (CSS does the slide; this file only supports it):
    - after an add to cart the drawer is held off screen (data-onx-hold) until the new lines are drawn, then it slides in
      (a CSS animation releases the hold after 1.6s even if this file never runs; a timer here releases it after 1.5s)
    - lines are redrawn by the theme on every change: the previous <img> elements are re-used (same key, same src) and
      images are forced to load eagerly, so nothing blinks
    - the theme removes its "is-transitioning" class on the first transitionend that bubbles up; a 450 ms fallback makes
      sure it can never stay stuck after an open or close
  Focus: the theme's focus trap only wraps forward (last to first). This file adds the backward wrap (Shift+Tab on the
    first control or on the drawer itself goes to the last control) so keyboard focus can never leave the open drawer.
  And one thing around the theme's own window.alert() calls: the theme reports "quantity above stock" and "agree to
  the terms" with a browser alert. While the drawer is open and right after a quantity change or a checkout click, that
  message is shown inline in the drawer (role="alert") instead. Any other alert goes to the native one.
*/
(function () {
  'use strict';

  if (window.ONXCartDrawer) { return; }

  var drawer, form, products, countEl, liveEl, errEl, observer, closeObserver;
  var firstRender = true;
  var armedAt = 0;
  var ARM_MS = 10000;
  var nativeAlert = window.alert;
  var errTimer = null;
  var holdTimer = null;
  var transTimer = null;

  function parseMoney(text) {
    var s = String(text || '').replace(/[^\d.,]/g, '');
    if (!s) { return NaN; }
    if (/,\d{1,2}$/.test(s) && !/\.\d{1,2}$/.test(s)) { s = s.replace(/\./g, '').replace(',', '.'); }
    else { s = s.replace(/,/g, ''); }
    return parseFloat(s);
  }

  function isOpen() {
    return !!(drawer && drawer.classList.contains('drawer--is-open'));
  }

  function clearError() {
    if (errTimer) { clearTimeout(errTimer); errTimer = null; }
    if (errEl && !errEl.hidden) { errEl.hidden = true; errEl.textContent = ''; }
  }

  function showError(message) {
    if (!errEl) { return false; }
    errEl.textContent = message;
    errEl.hidden = false;
    if (errTimer) { clearTimeout(errTimer); }
    errTimer = setTimeout(clearError, 12000);
    return true;
  }

  function arm() { armedAt = Date.now(); }


  var productCache = {};
  var cartSeq = 0;

  function fmt(cents) {
    var t = window.theme;
    try {
      if (t && t.Currency && t.settings && t.settings.moneyFormat) { return t.Currency.formatMoney(cents, t.settings.moneyFormat); }
    } catch (e) { /* fall through */ }
    return '$' + (cents / 100).toFixed(2);
  }

  // variant id -> compare_at_price (cents) for a product handle, cached per page; null when unavailable
  function compareMap(handle) {
    if (!handle) { return Promise.resolve(null); }
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (!j || !j.variants) { return null; }
          var m = {};
          j.variants.forEach(function (v) { m[v.id] = v.compare_at_price ? Number(v.compare_at_price) : 0; });
          return m;
        })
        .catch(function () { return null; })
        .then(function (m) { compareCache[handle] = m; return m; });
    }
    return productCache[handle];
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) { e.className = cls; }
    if (text != null) { e.textContent = text; }
    return e;
  }

  var compareCache = {};   // handle -> {variantId: compare_at cents} | null (resolved), filled by compareMap()

  function handleOf(row) {
    var a = row.querySelector('.cart__item-name');
    var m = a && (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function variantOf(row) {
    var a = row.querySelector('.cart__item-name');
    var m = a && (a.getAttribute('href') || '').match(/[?&]variant=(\d+)/);
    return m ? Number(m[1]) : null;
  }
  function cents(text) { var n = parseMoney(text); return isNaN(n) ? NaN : Math.round(n * 100); }

  // Synchronous pass: numbers read from the lines the theme just drew, compare at from the cache. No network, so the
  // sale display is there in the same frame as the lines (no price pop during the slide or after a quantity change).
  function paintFromDom(row) {
    var handle = handleOf(row), vid = variantOf(row);
    if (!handle || !(handle in compareCache)) { return false; }
    var num = row.querySelector('.js-qty__num');
    var qty = num ? parseInt(num.getAttribute('value'), 10) : NaN;
    var strike = row.querySelector('.cart__price--strikethrough');
    var cur = row.querySelector('.cart__item-price-col .cart__price:not(.cart__price--strikethrough)');
    if (!cur || isNaN(qty) || qty < 1) { return false; }
    var finalUnit = cents(cur.textContent), origUnit = strike ? cents(strike.textContent) : finalUnit;
    if (isNaN(finalUnit) || isNaN(origUnit)) { return false; }
    var m = compareCache[handle];
    paint(row, { quantity: qty, original_price: origUnit, final_price: finalUnit, final_line_price: finalUnit * qty }, m && vid ? (m[vid] || 0) : 0);
    return true;
  }

  function priceLines() {
    if (!products) { return Promise.resolve(); }
    var rows = Array.prototype.slice.call(products.querySelectorAll('.cart__item[data-key]'));
    if (!rows.length) { return Promise.resolve(); }
    var seq = ++cartSeq;
    var waits = [];
    rows.forEach(function (row) {
      if (paintFromDom(row)) { return; }
      var h = handleOf(row);
      if (!h) { return; }
      waits.push(compareMap(h).then(function () { if (seq === cartSeq) { paintFromDom(row); } }));
    });
    // exact cents from /cart.js (line level discount rounding); repaints a line only when it differs
    var exact = fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      if (seq !== cartSeq || !cart || !cart.items) { return; }
      var byKey = {};
      cart.items.forEach(function (it) { byKey[it.key] = it; });
      rows.forEach(function (row) {
        var it = byKey[row.getAttribute('data-key')];
        if (!it) { return; }
        var m = compareCache[it.handle];
        var cmp = m ? (m[it.variant_id] || 0) : 0;
        var sig = [it.quantity, it.original_price, it.final_price, it.final_line_price, cmp].join('|');
        if (row.getAttribute('data-onx-sig') !== sig || !row.hasAttribute('data-onx-priced')) { paint(row, it, cmp); }
      });
    }).catch(function () { /* keep what is painted */ });
    // the drawer is released as soon as every line is painted from the page; the exact pass finishes in the background
    return Promise.all(waits).then(function () { return exact; }).catch(function () {});
  }

  function paint(row, it, cmp) {
    var col = row.querySelector('.cart__item-price-col');
    if (!col) { return; }
    var qty = it.quantity;
    var was = Math.max(cmp || 0, it.original_price);         // per unit, before any discount
    var now = it.final_price;                                  // per unit, after line level discounts
    var sale = now < was;
    var regular = drawer.getAttribute('data-onx-regular-label') || 'Regular price';
    var saleLabel = drawer.getAttribute('data-onx-sale-label') || 'Sale price';

    // keep what the theme prints for discounts and unit prices (names and amounts), rebuild only the price spans
    var keep = Array.prototype.filter.call(col.children, function (c) {
      return (c.tagName === 'SMALL' && c.classList.contains('cart__discount') && !c.classList.contains('cart__price')) || c.classList.contains('product__unit-price');
    });
    while (col.firstChild) { col.removeChild(col.firstChild); }
    if (sale) {
      col.appendChild(el('span', 'visually-hidden', regular));
      col.appendChild(el('s', 'cart__price cart__price--strikethrough', fmt(was * qty)));
      col.appendChild(el('span', 'visually-hidden', saleLabel));
      col.appendChild(el('span', 'cart__price cart__discount', fmt(it.final_line_price)));
    } else {
      col.appendChild(el('span', 'cart__price', fmt(it.final_line_price)));
    }
    keep.forEach(function (k) { col.appendChild(k); });

    var title = row.querySelector('.cart__item-title');
    var old = row.querySelector('.onx-cd__tag');
    if (old) { old.parentNode.removeChild(old); }
    if (sale && title && was > 0) {
      var pct = Math.max(1, Math.round((was - now) / was * 100));
      var tag = el('span', 'onx-cd__tag', '-' + pct + '%');
      title.appendChild(tag);
    }
    row.setAttribute('data-onx-priced', '1');
    row.setAttribute('data-onx-sig', [it.quantity, it.original_price, it.final_price, it.final_line_price, cmp || 0].join('|'));
  }


  function hold(evt) {
    // start fetching the compare at price of the product that was just added, in parallel with the theme's own fetch
    try { var pr = evt && evt.detail && evt.detail.product; if (pr && pr.handle) { compareMap(pr.handle); } } catch (e) { /* ignore */ }
    if (!drawer || isOpen()) { return; }
    drawer.setAttribute('data-onx-hold', '1');
    if (holdTimer) { clearTimeout(holdTimer); }
    holdTimer = setTimeout(release, 1500);
  }

  function release() {
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
    if (drawer) { drawer.removeAttribute('data-onx-hold'); }
  }

  // re-use the already decoded <img> of a line the theme just redrew (same key, same src): no reload, no blink
  function reuseImages(records) {
    try {
      var old = {};
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.removedNodes, function (n) {
          if (n.querySelectorAll) { Array.prototype.forEach.call(n.querySelectorAll('.cart__item[data-key]'), function (it) { old[it.getAttribute('data-key')] = it; }); }
        });
      });
      Array.prototype.forEach.call(products.querySelectorAll('.cart__item[data-key]'), function (it) {
        var o = old[it.getAttribute('data-key')];
        if (!o) { return; }
        var oi = o.querySelector('.cart__image img'), ni = it.querySelector('.cart__image img');
        if (oi && ni && oi.complete && oi.naturalWidth > 0 && (oi.getAttribute('src') === ni.getAttribute('src'))) {
          var oc = o.querySelector('.cart__image'), nc = it.querySelector('.cart__image');
          if (oc && nc && nc.parentNode) { nc.parentNode.replaceChild(oc, nc); }
        }
      });
      Array.prototype.forEach.call(products.querySelectorAll('.cart__image img[loading="lazy"]'), function (im) { im.loading = 'eager'; });
    } catch (e) { /* cosmetic only */ }
  }

  function enhance() {
    if (!products) { return; }
    var wrap = products.querySelector('.cart__items');
    var count = wrap ? parseInt(wrap.getAttribute('data-count'), 10) : NaN;
    if (isNaN(count)) { count = 0; }

    if (countEl) { countEl.textContent = count > 0 ? ' (' + count + ')' : ''; }

    var removeLabel = drawer.getAttribute('data-onx-remove-label') || 'Remove';
    var reduceLabel = drawer.getAttribute('data-onx-reduce-label') || 'Reduce item quantity by one';

    Array.prototype.forEach.call(products.querySelectorAll('.cart__item'), function (item) {
      var minus = item.querySelector('.js-qty__adjust--minus');
      var num = item.querySelector('.js-qty__num');
      if (minus && num) {
        var one = parseInt(num.getAttribute('value'), 10) === 1;
        minus.setAttribute('aria-label', one ? removeLabel : reduceLabel);
      }
    });

    var priced = priceLines();

    if (liveEl && !firstRender) {
      var sub = drawer.querySelector('[data-subtotal]');
      liveEl.textContent = '';
      var msg = count > 0
        ? 'Cart updated. ' + count + (count === 1 ? ' item' : ' items') + ', subtotal ' + (sub ? sub.textContent.trim() : '') + '.'
        : 'Your cart is empty.';
      setTimeout(function () { liveEl.textContent = msg; }, 60);
    }
    firstRender = false;
    return priced;
  }


  function tabbables() {
    return Array.prototype.filter.call(drawer.querySelectorAll('a[href], button, input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex^="-"])'), function (e) {
      return !e.disabled && e.tabIndex >= 0 && (e.offsetParent !== null || e.getClientRects().length > 0) && getComputedStyle(e).visibility !== 'hidden';
    });
  }

  function onKeydown(evt) {
    if (evt.key !== 'Tab' || !isOpen() || !drawer.contains(document.activeElement) && document.activeElement !== document.body) { return; }
    var list = tabbables();
    if (!list.length) { return; }
    var cur = document.activeElement, first = list[0], last = list[list.length - 1];
    if (evt.shiftKey && (cur === first || cur === form || cur === drawer || cur === document.body)) { evt.preventDefault(); last.focus(); }
    else if (!evt.shiftKey && cur === last) { evt.preventDefault(); first.focus(); }
  }

  function init() {
    drawer = document.getElementById('CartDrawer');
    if (!drawer || !drawer.classList.contains('onx-cd')) { return; }
    form = document.getElementById('CartDrawerForm');
    products = drawer.querySelector('[data-products]');
    countEl = drawer.querySelector('[data-onx-cd-count]');
    liveEl = drawer.querySelector('[data-onx-cd-live]');
    errEl = drawer.querySelector('[data-onx-cd-error]');

    if (products && window.MutationObserver) {
      observer = new MutationObserver(function (records) {
        try { reuseImages(records); var pr = enhance(); (pr && pr.then ? pr : Promise.resolve()).then(release, release); } catch (e) { release(); /* never break the cart */ }
      });
      observer.observe(products, { childList: true });
    }
    try { enhance(); } catch (e) { /* ignore */ }

    // a new quantity change or checkout click clears the old message and lets the next alert show inline
    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('ajaxProduct:added', hold, true);
    document.addEventListener('cart:quantity.cart-cart-drawer', function () { clearError(); arm(); });
    if (form) { form.addEventListener('submit', arm, true); }
    var checkout = drawer.querySelector('.cart__checkout');
    if (checkout) { checkout.addEventListener('click', arm, true); }

    if (window.MutationObserver) {
      closeObserver = new MutationObserver(function () {
        if (!isOpen()) { clearError(); release(); }
        if (transTimer) { clearTimeout(transTimer); }
        transTimer = setTimeout(function () { drawer.classList.remove('is-transitioning'); }, 450);
      });
      closeObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    window.alert = function (message) {
      try {
        if (isOpen() && (Date.now() - armedAt) < ARM_MS && showError(String(message))) { return; }
      } catch (e) { /* fall through to the native alert */ }
      return nativeAlert.apply(window, arguments);
    };
  }

  window.ONXCartDrawer = { refresh: function () { try { enhance(); } catch (e) { /* ignore */ } } };

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
})();
